import { useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CalendarCheck, Send } from "lucide-react";
import { useLang } from "../lib/i18n";
import { CallButton, WhatsAppButton } from "../components/Buttons";
import { HERO_VIDEO } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaskedLine = ({ children, delay, testid }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      data-testid={testid}
    >
      {children}
    </motion.span>
  </span>
);

export const scrollToSection = (sel) => {
  const el = document.querySelector(sel);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { t, lang } = useLang();
  const [form, setForm] = useState({ naam: "", telefoon: "", email: "", bericht: "" });
  const [loading, setLoading] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.toastOk);
      setForm({ naam: "", telefoon: "", email: "", bericht: "" });
    } catch {
      toast.error(t.toastErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pb-20 pt-44 md:pb-24 md:pt-48">
      <div className="absolute inset-0 bg-[#16130e]" aria-hidden="true" data-testid="hero-video-bg">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onLoadedData={() => setVideoReady(true)}
          className={`h-full w-full object-cover transition-opacity duration-1000 ${videoReady ? "opacity-100" : "opacity-0"}`}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#16130e]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#16130e]/60 via-[#16130e]/25 to-[#16130e]/85" />
      </div>
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute -left-40 -top-40 h-[720px] w-[720px] rounded-full bg-[#ffe9a8]/25 blur-[120px]" />
      <div className="glow-breathe pointer-events-none absolute right-[-10%] top-[30%] h-[420px] w-[420px] rounded-full bg-[#ffe9a8]/15 blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="hero-animated-bg">
        <div className="beam beam-a" />
        <div className="beam beam-b" />
        <div className="beam beam-c" />
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <span key={i} className={`bolt-float bolt-${i}`} />
        ))}
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:px-12 lg:grid-cols-12 lg:gap-10 lg:px-20">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-7 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute h-full w-full animate-ping rounded-full bg-amber opacity-60" />
              <span className="absolute h-full w-full animate-ping rounded-full bg-amber opacity-40 [animation-delay:0.6s]" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-amber" />
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-white/70">{t.heroEyebrow}</span>
          </motion.div>

          <h1 className="font-archivo text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.25} testid="hero-line-1">{t.heroL1}</MaskedLine>
            <MaskedLine delay={0.4} testid="hero-line-2">{t.heroL2}</MaskedLine>
            <MaskedLine delay={0.55} testid="hero-line-3">
              <span className="text-amber">{t.heroL3}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-7 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base"
            data-testid="hero-subline"
          >
            {t.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.button
              onClick={() => scrollToSection("#gesprek")}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="inline-flex items-center justify-center gap-2.5 rounded-[10px] bg-amber px-7 py-4 font-archivo text-base font-bold tracking-tight text-[#181510] transition-colors duration-300 hover:bg-amberdeep"
              data-testid="hero-intro-button"
            >
              <CalendarCheck size={18} strokeWidth={2.5} />
              {t.ctaIntro}
            </motion.button>
            <motion.button
              onClick={() => scrollToSection("#diensten")}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="inline-flex items-center justify-center gap-2.5 rounded-[10px] border border-white/25 bg-white/10 px-7 py-4 font-archivo text-base font-bold tracking-tight text-white transition-colors duration-300 hover:border-white/50 hover:bg-white/15"
              data-testid="hero-services-button"
            >
              {t.ctaServices}
              <ArrowRight size={18} strokeWidth={2.5} />
            </motion.button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <div className="absolute -left-3 -top-3 h-full w-full rounded-[12px] border border-white/25" aria-hidden="true" />
          <form
            onSubmit={submit}
            className="relative rounded-[12px] border border-edge/70 bg-white/95 p-6 backdrop-blur-md md:p-7"
            data-testid="hero-contact-form"
          >
            <p className="font-archivo text-xl font-extrabold tracking-tight text-cream">{t.heroFormTitle}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-faint">{t.heroFormSub}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <CallButton label={t.callNow} testid="hero-call-button" className="px-4 py-3 text-sm" />
              <WhatsAppButton label={t.whatsappUs} testid="hero-whatsapp-button" className="px-4 py-3 text-sm" />
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input required value={form.naam} onChange={set("naam")} placeholder={t.phName} aria-label={t.fName} className="input-field" data-testid="hero-input-naam" />
              <input required type="tel" value={form.telefoon} onChange={set("telefoon")} placeholder={t.phPhone} aria-label={t.fPhone} className="input-field" data-testid="hero-input-telefoon" />
            </div>
            <input required type="email" value={form.email} onChange={set("email")} placeholder={t.phEmail} aria-label={t.fEmail} className="input-field mt-4" data-testid="hero-input-email" />
            <textarea required rows={3} value={form.bericht} onChange={set("bericht")} placeholder={t.phMsg} aria-label={t.fMsg} className="input-field mt-4 resize-none" data-testid="hero-input-bericht" />
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: 1 }}
              className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-amber px-7 py-3.5 font-archivo text-base font-bold tracking-tight text-[#181510] transition-colors duration-300 hover:bg-amberdeep disabled:opacity-60"
              data-testid="hero-submit-button"
            >
              <Send size={16} strokeWidth={2.5} />
              {loading ? t.sending : t.submit}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
